import { createContext, useContext, useState, useEffect } from 'react';
import { api } from '@/utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BrandingContext = createContext(null);

export function BrandingProvider({ children }) {
  // Initialize from localStorage cache - no hardcoded defaults
  const [branding, setBranding] = useState(() => {
    try {
      const cached = localStorage.getItem('app_branding');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed?.logo_url) return parsed;
      }
    } catch {}
    return { logo_url: '', logo_text: 'Bus & Hire' };
  });

  const [footer, setFooter] = useState(() => {
    try {
      const cached = localStorage.getItem('app_footer');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });

  const [seo, setSeo] = useState(() => {
    try {
      const cached = localStorage.getItem('app_seo');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });

  useEffect(() => {
    // Fetch fresh data once on app load
    const fetchContent = async () => {
      try {
        const content = await api.getContent();
        if (content.branding?.logo_url) {
          setBranding(content.branding);
          localStorage.setItem('app_branding', JSON.stringify(content.branding));
        }
        if (content.footer) {
          setFooter(content.footer);
          localStorage.setItem('app_footer', JSON.stringify(content.footer));
        }
        if (content.seo) {
          setSeo(content.seo);
          localStorage.setItem('app_seo', JSON.stringify(content.seo));
        }
      } catch (error) {
        console.error('Error fetching branding:', error);
      }
    };

    // Also fetch from public branding API for driver app
    const fetchPublicBranding = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/branding`);
        if (response.ok) {
          const data = await response.json();
          if (data.logo_url) {
            setBranding(prev => ({ ...prev, ...data }));
            localStorage.setItem('app_branding', JSON.stringify({ ...branding, ...data }));
          }
        }
      } catch (error) {
        console.error('Error fetching public branding:', error);
      }
    };

    fetchContent();
    fetchPublicBranding();
  }, []);

  // Compute full logo URL - handle relative paths
  const logoUrl = branding?.logo_url || '';
  const fullLogoUrl = logoUrl && logoUrl.startsWith('/') ? `${API_URL}${logoUrl}` : logoUrl;

  const value = {
    branding,
    footer,
    seo,
    logoUrl: fullLogoUrl,
    logoText: branding?.logo_text || 'Bus & Hire'
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const context = useContext(BrandingContext);
  if (!context) {
    // Return empty defaults if used outside provider
    return {
      branding: { logo_url: '', logo_text: 'Bus & Hire' },
      footer: null,
      seo: null,
      logoUrl: '',
      logoText: 'Bus & Hire'
    };
  }
  return context;
}
